export class ApiError extends Error {
  constructor(message, metadata = {}) {
    super(message);
    this.name = "ApiError";
    Object.assign(this, metadata);
    this.response = {
      status: metadata.status,
      data: metadata.data,
      headers: metadata.headers,
    };
  }
}

export function isAbortError(error) {
  return error?.name === "AbortError" || error?.code === "ABORT_ERR";
}

export function getApiErrorMessage(error, fallback = "Сталася помилка.") {
  if (isAbortError(error)) return "";

  const data = error?.response?.data ?? error?.data;
  let message;
  if (typeof data === "string" && data.trim()) {
    message = data.trim();
  } else if (data && typeof data === "object") {
    message = [data.detail, data.title, data.message, data.error]
      .find((value) => typeof value === "string" && value.trim())?.trim();
  }

  message ||= typeof error?.message === "string" && error.message.trim()
    ? error.message.trim()
    : fallback;

  const status = error?.status ?? error?.response?.status;
  const traceId = getApiTraceId(error);
  return status >= 500 && traceId ? `${message} Код: ${traceId}` : message;
}

export function getApiTraceId(error) {
  const data = error?.response?.data ?? error?.data;
  const headers = error?.response?.headers ?? error?.headers;
  const bodyTrace = data && typeof data === "object" && typeof data.traceId === "string"
    ? data.traceId
    : null;
  return bodyTrace || readHeader(headers, "trace-id") || readHeader(headers, "x-trace-id") || null;
}

export function getApiValidationErrors(error) {
  const data = error?.response?.data ?? error?.data;
  if (!data || typeof data !== "object" || !data.errors || typeof data.errors !== "object") return {};

  return Object.fromEntries(Object.entries(data.errors).flatMap(([field, messages]) => {
    const safe = Array.isArray(messages)
      ? messages.filter((message) => typeof message === "string")
      : typeof messages === "string" ? [messages] : [];
    return safe.length ? [[field, safe]] : [];
  }));
}

export function getApiRetryAfter(error) {
  const value = error?.retryAfter ?? readHeader(error?.response?.headers ?? error?.headers, "retry-after");
  if (value == null || value === "") return null;
  const seconds = Number(value);
  return Number.isFinite(seconds) ? seconds : value;
}

export async function createApiError(response, fallback) {
  const data = await parseResponseBody(response);
  const headers = response.headers;
  const error = new ApiError("", {
    status: response.status,
    data,
    headers,
    traceId: data && typeof data === "object" ? data.traceId : null,
    validationErrors: data && typeof data === "object" ? data.errors : null,
    code: data && typeof data === "object" && typeof data.code === "string" ? data.code : null,
    retryAfter: readHeader(headers, "retry-after"),
  });
  error.message = getApiErrorMessage(error, fallback || `HTTP ${response.status}`);
  return error;
}

async function parseResponseBody(response) {
  try {
    const text = await response.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch { return text; }
  } catch {
    return null;
  }
}

function readHeader(headers, name) {
  if (!headers) return null;
  if (typeof headers.get === "function") return headers.get(name);
  const key = Object.keys(headers).find((item) => item.toLowerCase() === name);
  return key ? headers[key] : null;
}
